<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <base-widget
    icon="file-text"
    title="Company Documents"
    :loading="isLoading"
    :empty="!isLoading && documents.length === 0"
  >
    <div class="orangehrm-company-documents-widget">
      <div class="orangehrm-company-info">
        <oxd-text class="orangehrm-company-subtitle">
          Company Resources & Policies
        </oxd-text>
      </div>

      <div class="orangehrm-document-links">
        <div
          v-for="document in documents"
          :key="document.id"
          class="orangehrm-document-item"
        >
          <oxd-icon
            :name="getDocumentIcon(document.value)"
            class="orangehrm-document-icon"
          />
          <oxd-text
            tag="a"
            :href="document.value"
            target="_blank"
            rel="noopener noreferrer"
            class="orangehrm-document-link"
          >
            {{ document.name }}
          </oxd-text>
        </div>
      </div>

      <div v-if="documents.length > 0" class="orangehrm-widget-footer">
        <oxd-text class="orangehrm-footer-text">
          Click any link to access resources
        </oxd-text>
      </div>
    </div>
  </base-widget>
</template>

<script>
import {ref, onMounted} from 'vue';
import {APIService} from '@/core/util/services/api.service';
import BaseWidget from '@/orangehrmDashboardPlugin/components/BaseWidget.vue';

export default {
  name: 'CompanyDocumentsWidget',
  components: {
    'base-widget': BaseWidget,
  },
  props: {
    companies: {
      type: Array,
      default: () => [],
    },
  },
  setup(props) {
    const isLoading = ref(false);
    const documents = ref([...props.companies]);

    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/core/company-documents',
    );

    const loadDocuments = async () => {
      isLoading.value = true;
      try {
        const response = await http.getAll();
        if (response?.data?.data) {
          documents.value = response.data.data;
        }
      } catch {
        if (props.companies?.length) {
          documents.value = props.companies;
        }
      } finally {
        isLoading.value = false;
      }
    };

    const getDocumentIcon = (url) => {
      if (!url) {
        return 'file-text';
      }
      const lowerUrl = url.toLowerCase();
      if (lowerUrl.includes('.pdf') || lowerUrl.includes('drive.google')) {
        return 'file-earmark-pdf';
      }
      return 'globe';
    };

    onMounted(() => {
      loadDocuments();
    });

    return {
      isLoading,
      documents,
      getDocumentIcon,
    };
  },
};
</script>

<style lang="scss" scoped>
.orangehrm-company-documents-widget {
  padding: 1rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 0;
}

.orangehrm-company-info {
  text-align: center;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e5e7eb;
}

.orangehrm-company-subtitle {
  color: #6b7280;
  font-size: 0.875rem;
}

.orangehrm-document-links {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  overflow-y: auto;
  min-height: 0;
}

.orangehrm-document-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 0.5rem;
  background-color: #fff;
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
  word-break: break-word;

  &:hover {
    background-color: #fef7f0;
    border-color: var(--oxd-primary-one-color, #ff7b1a);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }
}

.orangehrm-document-icon {
  color: var(--oxd-primary-one-color, #ff7b1a);
  font-size: 1.25rem;
  flex-shrink: 0;
}

.orangehrm-document-link {
  color: #374151;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.875rem;
  transition: color 0.2s ease;
  flex: 1;

  &:hover {
    color: var(--oxd-primary-one-color, #ff7b1a);
    text-decoration: underline;
  }
}

.orangehrm-widget-footer {
  text-align: center;
  padding-top: 0.75rem;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.orangehrm-footer-text {
  color: #6b7280;
  font-size: 0.75rem;
  font-style: italic;
}

@media (max-width: 480px) {
  .orangehrm-company-documents-widget {
    padding: 0.75rem;
  }

  .orangehrm-document-item {
    padding: 0.625rem;
  }
}
</style>
